import React, { useMemo } from 'react';
import type { CandidateOpportunityOnboarding } from '@axiom/validation';
import {
  Accordions,
  DateUtil,
  Grid,
  GridColumn,
  GridRow,
  useApi,
  useOnboardingText,
} from '@axiom/ui';

import { EnvUtil } from '../../utils/env-util';
import { PermissionsApi } from '../../api/permissions-api';
import { TaxonomyApi } from '../../api/taxonomy-api';

import { EngagementDetailsFirstDay } from './EngagementDetailsFirstDay';
import { EngagementDetailsWorkSchedule } from './EngagementDetailsWorkSchedule';
import { EngagementDetailsCompBenefits } from './EngagementDetailsCompBenefits';
import { EngagementDetailsLegal } from './EngagementDetailsLegal';
import { EngagementDetailsLegalResources } from './EngagementDetailsLegalResources';

export type OnboardingProps = {
  name?: string;
  data: CandidateOpportunityOnboarding;
};

export const EngagementDetails = ({ name, data }: OnboardingProps) => {
  const position = data.positions?.[0];

  const [{ data: permissions } = { data: null }, { data: taxonomy }] = useApi(
    position?.candidateOpportunities?.[0]?.candidate.id
      ? PermissionsApi.readPermissions(
          position.candidateOpportunities[0].candidate.id
        )
      : undefined,
    TaxonomyApi.readTaxonomy()
  );

  const { getOnboardingTexts, formatEngagementAddress } = useOnboardingText({
    data,
    urls: {
      onboardingTimeEntryUrl: EnvUtil.onboardingTimeEntryUrl,
      onboardingLinkedinGuideUrl: EnvUtil.onboardingLinkedinGuideUrl,
      onboardingAodaInstructionsUrl: EnvUtil.onboardingAodaInstructionsUrl,
      onboardingLsoLawproUrl: EnvUtil.onboardingLsoLawproUrl,
      onboardingLawSocietyGuideUrl: EnvUtil.onboardingLawSocietyGuideUrl,
      onboardingEngagementSuccessUrl: EnvUtil.onboardingEngagementSuccessUrl,
      onboardingEncryptedEmailUrl: EnvUtil.onboardingEncryptedEmailUrl,
      workdayHelpcenterUrl: permissions?.workday
        ? EnvUtil.workdayHelpcenterUrl
        : null,
    },
    taxonomy,
  });

  const texts = getOnboardingTexts(position);

  const location = useMemo(() => {
    return formatEngagementAddress(false).join(' ');
  }, [data, formatEngagementAddress]);

  return (
    <Grid name={name ?? 'ONBOARDING'}>
      {DateUtil.getDaysSinceNow(position?.startDate ?? new Date()) < 10 ? (
        <GridRow gutterBottom="24px">
          <GridColumn>
            <EngagementDetailsFirstDay
              name="ONBOARDING_FIST_DAY"
              position={position}
              location={location}
              texts={{
                setUpSuccess: texts?.setUpSuccess ?? null,
                startedWithAxiom: texts?.startedWithAxiom ?? null,
              }}
            />
          </GridColumn>
        </GridRow>
      ) : null}
      <GridRow>
        <GridColumn>
          <Accordions name="ONBOARDING_INFO">
            <EngagementDetailsWorkSchedule
              name="WORK_SCHEDULE"
              texts={{
                weeklyCommitment: texts?.weeklyCommitment ?? null,
                overtime: texts?.overtime ?? null,
              }}
            />
            <EngagementDetailsCompBenefits
              name="COMP_BENEFITS"
              position={position}
              texts={{
                benefits: texts?.benefits ?? null,
                holiday: texts?.holiday ?? null,
              }}
            />
            {texts?.resources ? (
              <EngagementDetailsLegalResources
                name="LEGAL_RESOURCES"
                texts={{
                  resourcesText: texts.resources,
                }}
              />
            ) : null}

            <EngagementDetailsLegal
              name="LEGAL"
              texts={{
                describingAxiom: texts?.describingAxiom ?? null,
                supportResources: texts?.supportResources ?? null,
                communications: texts?.communications ?? null,
                workLocationWorkingRemotely:
                  texts?.workLocationWorkingRemotely ?? null,
              }}
            />
          </Accordions>
        </GridColumn>
      </GridRow>
    </Grid>
  );
};
